import React, { useRef, useEffect, useState } from "react";
import { Arrow, Line, Circle, Group } from "react-konva";
import { Html } from "react-konva-utils";
import PropTypes from "prop-types";
import "../styles.css";
import { isPrimaryMouseButton } from "../../utils/pointerButtonUtils";
import { constrainOffCanvasPosition } from "../../utils/dragEndUtils";

function normalizeToGroup(points = []) {
  let minX = Infinity,
    minY = Infinity;
  for (let i = 0; i < points.length; i += 2) {
    minX = Math.min(minX, points[i]);
    minY = Math.min(minY, points[i + 1]);
  }

  const relativePoints = points.map((val, idx) =>
    idx % 2 === 0 ? val - minX : val - minY
  );

  return { relativePoints, offsetX: minX, offsetY: minY };
}

const calculateLengthAndAngle = (x1, y1, x2, y2) => {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const length = Math.sqrt(dx * dx + dy * dy);
  const angle = (Math.atan2(dy, dx) * 180) / Math.PI;
  return { length, angle };
};

const LineElement = ({
  line,
  isDraggable,
  onClick,
  onDragEnd,
  onSelect,
  isSelected,
  onPointsUpdate,
  onDragMove,
  saveToHistory,
  onTransform,
  onDotDragMove,
  setSelectedObject,
  sourceStageRef,
  effectiveZoomLevel = 1, // Zoom level for scaling elements
  isOffCanvas = false,
  isPreview = false,
  onCopyToOppositeCanvas,
  canvasWidth,
  canvasHeight,
  isElementBeingEdited = false,
  setPendingChanges = () => {},
}) => {
  const {
    id,
    points: absolutePoints,
    stroke: strokeColor = "#000",
    strokeWidth = 2,
    isDashed = false,
    startDot,
    endDot,
    x: initialGroupX = 0,
    y: initialGroupY = 0,
  } = line;

  const groupRef = useRef();
  const lineRef = useRef();
  const startDotRef = useRef();
  const endDotRef = useRef();
  const isDraggingRef = useRef(false); // Track if we're currently dragging

  // Use points directly as relative coordinates (they are now consistently relative)
  const relativePoints = line.points || [0, 0, 0, 0];

  const currentPointsRef = useRef(relativePoints);
  const [currentPoints, setCurrentPoints] = useState(relativePoints);
  const [_, forceUpdate] = useState(0);
  const lastSelectUpdateRef = useRef(0); // Add throttling for onSelect calls
  const storedWidthHeightRef = useRef({
    width: line.width,
    height: line.height,
  }); // Store width/height during drag

  useEffect(() => {
    // Update points when they change - use points directly as they are now relative

    const newRelativePoints = line.points || [0, 0, 0, 0];
    currentPointsRef.current = newRelativePoints;
    setCurrentPoints(newRelativePoints);
  }, [line.points, line.x, line.y]);

  // Force position update when line position (x, y) changes (for redo/undo)
  // Skip this during drag operations to prevent position conflicts
  useEffect(() => {
    // Don't update position if we're currently dragging
    if (isDraggingRef.current) {
      return;
    }
    // Add a small delay to ensure the component has fully re-rendered
    setTimeout(() => {
      if (groupRef.current && !isDraggingRef.current) {
        const newX = line.x || 0;
        const newY = line.y || 0;

        groupRef.current.x(newX);
        groupRef.current.y(newY);
        groupRef.current.getLayer()?.batchDraw();
      } else {
      }
    }, 50);
  }, [line.x, line.y, line.id]); // Only depend on position and id, not the entire line object

  // Additional force update for redo operations
  useEffect(() => {
    if (line.isFromHistory || line.isRestored) {
      setTimeout(() => {
        if (groupRef.current) {
          const newX = line.x || 0;
          const newY = line.y || 0;

          groupRef.current.x(newX);
          groupRef.current.y(newY);
          groupRef.current.getLayer()?.batchDraw();
        }
      }, 100);
    }
  }, [line.isFromHistory, line.isRestored, line.x, line.y]);

  // Debug line position and visibility
  useEffect(() => {}, [line.x, line.y, line.points]);

  // Add min/max constants for width/height
  const MIN_WIDTH = 30;
  const MAX_WIDTH = 570;
  const MIN_HEIGHT = 0;
  const MAX_HEIGHT = 600;

  // Utility to clamp and normalize a line to allowed min/max box
  function clampAndNormalizeLine(x0, y0, x1, y1) {
    let dx = x1 - x0;
    let dy = y1 - y0;
    let width = Math.abs(dx);
    let height = Math.abs(dy);
    let signX = dx >= 0 ? 1 : -1;
    let signY = dy >= 0 ? 1 : -1;
    let clampedWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, width));
    let clampedHeight = Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, height));
    // Always enforce clamped width/height in the returned points
    let newX1 = x0 + signX * clampedWidth;
    let newY1 = y0 + signY * clampedHeight;
    // Normalize so group is at top-left
    let minX = Math.min(x0, newX1);
    let minY = Math.min(y0, newY1);
    let points = [x0 - minX, y0 - minY, newX1 - minX, newY1 - minY];
    return {
      points,
      groupX: minX,
      groupY: minY,
      absPoints: [x0, y0, newX1, newY1],
    };
  }

  const updatePoint = (index, pointX, pointY) => {
    // 1. Get current group position (use ref during drag, fallback to prop)
    const groupX = groupRef.current?.x() ?? line.x ?? 0;
    const groupY = groupRef.current?.y() ?? line.y ?? 0;

    // 2. Update the dragged point in relative coordinates
    const currentRelativePoints = [...currentPointsRef.current];

    // 3. Apply boundary constraints for main canvas - prevent control points from going outside
    let constrainedPointX = pointX;
    let constrainedPointY = pointY;

    if (!isOffCanvas && canvasWidth && canvasHeight) {
      const canvasPadding = 20;

      // Calculate absolute position of the dragged point
      const absPointX = pointX + groupX;
      const absPointY = pointY + groupY;

      // Constrain the point within canvas boundaries
      const constrainedAbsX = Math.max(
        canvasPadding,
        Math.min(canvasWidth - canvasPadding, absPointX)
      );
      const constrainedAbsY = Math.max(
        canvasPadding,
        Math.min(canvasHeight - canvasPadding, absPointY)
      );

      // Convert back to relative coordinates
      constrainedPointX = constrainedAbsX - groupX;
      constrainedPointY = constrainedAbsY - groupY;
    }

    currentRelativePoints[index] = constrainedPointX;
    currentRelativePoints[index + 1] = constrainedPointY;

    // 4. Update the current points reference and state
    currentPointsRef.current = currentRelativePoints;
    setCurrentPoints(currentRelativePoints);

    // 5. Update the visual line
    if (lineRef.current) {
      lineRef.current.points(currentRelativePoints);
    }

    // 6. Update the visual circles
    if (startDotRef.current) {
      startDotRef.current.x(currentRelativePoints[0]);
      startDotRef.current.y(currentRelativePoints[1]);
    }
    if (endDotRef.current) {
      endDotRef.current.x(currentRelativePoints[2]);
      endDotRef.current.y(currentRelativePoints[3]);
    }

    // 7. Force a redraw without re-rendering the component
    if (groupRef.current) {
      groupRef.current.getLayer().batchDraw();
    }

    // 6. Update the selected object with new dimensions if line is selected
    if (isSelected && onSelect) {
      // Throttle onSelect calls to prevent too frequent updates during drag
      const now = Date.now();
      const throttleDelay = 16; // Reduced to ~60fps for smoother updates

      if (now - lastSelectUpdateRef.current > throttleDelay) {
        // Calculate dimensions from the updated points
        let minX = Infinity,
          minY = Infinity,
          maxX = -Infinity,
          maxY = -Infinity;
        const pts = currentRelativePoints;
        // Use current group position instead of line.x/y which might be outdated
        const groupX = groupRef.current?.x() || line.x || 0;
        const groupY = groupRef.current?.y() || line.y || 0;

        for (let i = 0; i < pts.length; i += 2) {
          const x = pts[i] + groupX;
          const y = pts[i + 1] + groupY;
          minX = Math.min(minX, x);
          maxX = Math.max(maxX, x);
          minY = Math.min(minY, y);
          maxY = Math.max(maxY, y);
        }

        const [absX1, absY1, absX2, absY2] = pts.map((val, idx) =>
          idx % 2 === 0 ? val + groupX : val + groupY
        );
        const { length, angle } = calculateLengthAndAngle(
          absX1,
          absY1,
          absX2,
          absY2
        );

        // Determine type based on arrow head properties
        const pointerAtBeginning = line.pointerAtBeginning || false;
        const pointerAtEnd = line.pointerAtEnd || false;
        let elementType = "Line";
        if (pointerAtBeginning && pointerAtEnd) {
          elementType = "DoubleSidedArrow";
        } else if (pointerAtBeginning || pointerAtEnd) {
          elementType = "Arrow";
        }

        // Create updated line object with new dimensions
        const updatedLine = {
          id,
          type: elementType,
          points: pts.map((val, idx) =>
            idx % 2 === 0 ? val + groupX : val + groupY
          ),
          strokeColor,
          strokeWidth,
          isDashed,
          x: groupX,
          y: groupY,
          length,
          angle,
          width: maxX - minX,
          height: maxY - minY,
          rotation: 0,
          startDot,
          endDot,
          pointerAtBeginning,
          pointerAtEnd,
        };

        // Call onSelect to update the selected object
        onSelect(null, updatedLine);
        lastSelectUpdateRef.current = now;
      }
    }
  };

  // Remove the separate functions for dot dragging - use the same stable approach
  const updatePointForDotDragging = updatePoint;
  const updatePointPreservingGeometry = updatePoint;

  const handleDragMove = (e) => {
    const node = e.target;
    if (!node) return;
    let newX = node.x();
    let newY = node.y();

    if (isOffCanvas) {
      const elementStage = node.getStage();
      if (elementStage) {
        const pts = currentPointsRef.current;
        const pointerAtBeginning = line.pointerAtBeginning || false;
        const pointerAtEnd = line.pointerAtEnd || false;
        let elementType = "Line";
        if (pointerAtBeginning && pointerAtEnd) {
          elementType = "DoubleSidedArrow";
        } else if (pointerAtBeginning || pointerAtEnd) {
          elementType = "Arrow";
        }
        const merged = { ...line, points: pts, x: newX, y: newY };
        const r = constrainOffCanvasPosition(
          merged,
          elementType,
          newX,
          newY,
          elementStage,
        );
        newX = r.constrainedX;
        newY = r.constrainedY;
        if (newX !== node.x() || newY !== node.y()) {
          node.x(newX);
          node.y(newY);
        }
      }
    }

    // Mark that we're dragging to prevent position updates from useEffect
    isDraggingRef.current = true;

    // Calculate dimensions from current points for drag preview
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    const pts = currentPointsRef.current;
    for (let i = 0; i < pts.length; i += 2) {
      const x = pts[i] + newX;
      const y = pts[i + 1] + newY;
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
    }

    const width = maxX - minX;
    const height = maxY - minY;

    // Update the group position with all necessary properties for drag preview
    onDragMove?.(
      {
        ...line,
        id,
        x: newX,
        y: newY,
        points: currentPointsRef.current,
        width: width || line.width || 0,
        height: height || line.height || 0,
        rotation: line.rotation || 0,
      },
      e
    );
  };

  const handleDragEnd = (e) => {
    const node = groupRef.current;
    if (!node) return;
    const newX = node.x();
    const newY = node.y();

    // CRITICAL: Get current rotation from the Konva node, not from stale state
    // The node always has the current visual rotation
    let nodeRotation = node.rotation();
    nodeRotation = nodeRotation % 360;
    if (nodeRotation < 0) nodeRotation += 360;

    // Create currentLine with properties from node (rotation, scaleX, scaleY, position)
    // CRITICAL: Set rotation AFTER spreading line to ensure it overrides any stale values
    const currentLine = {
      ...line,
      x: newX,
      y: newY,
      // CRITICAL: These must come AFTER the spread to override any stale values from line
      rotation: nodeRotation, // Use the rotation from node
      scaleX: node.scaleX(),
      scaleY: node.scaleY(),
    };

    // Debug logging to verify rotation is preserved

    // Reset dragging flag
    isDraggingRef.current = false;

    onDragEnd(id, newX, newY, sourceStageRef, currentLine);
    // CRITICAL: Don't save to history here - history will be saved when element is unselected
    // This groups all drag and transform changes into a single history entry
  };

  const handleLineSelect = (e) => {
    // Determine type based on arrow head properties
    // Check both hasStartArrow/hasEndArrow and pointerAtBeginning/pointerAtEnd for compatibility
    const pointerAtBeginning = line.pointerAtBeginning || line.hasStartArrow || false;
    const pointerAtEnd = line.pointerAtEnd || line.hasEndArrow || false;

    let elementType = "Line";
    if (pointerAtBeginning && pointerAtEnd) {
      elementType = "DoubleSidedArrow";
    } else if (pointerAtBeginning || pointerAtEnd) {
      elementType = "Arrow";
    }

    // Set type in event target attrs
    e.target.attrs.type = elementType;
    e.target.attrs.id = line.id;
    e.target.attrs.name = line.name;
    e.target.attrs.strokeColor = line.stroke || strokeColor;
    e.target.attrs.x = line.x || 0;
    e.target.attrs.y = line.y || 0;
    e.target.attrs.points = currentPointsRef.current;
    if (!onSelect) return;

    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    const pts = currentPointsRef.current;

    for (let i = 0; i < pts.length; i += 2) {
      const x = pts[i] + (line.x || 0);
      const y = pts[i + 1] + (line.y || 0);
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
    }

    const [absX1, absY1, absX2, absY2] = pts.map((val, idx) =>
      idx % 2 === 0 ? val + (line.x || 0) : val + (line.y || 0)
    );
    const { length, angle } = calculateLengthAndAngle(
      absX1,
      absY1,
      absX2,
      absY2
    );

    const selectedLine = {
      id,
      type: elementType,
      points: pts.map((val, idx) =>
        idx % 2 === 0 ? val + (line.x || 0) : val + (line.y || 0)
      ),
      strokeColor,
      strokeWidth,
      isDashed,
      x: line.x || 0,
      y: line.y || 0,
      length,
      angle,
      width: maxX - minX,
      height: maxY - minY,
      rotation: 0,
      startDot,
      endDot,
      pointerAtBeginning,
      pointerAtEnd,
    };
    onSelect(e, selectedLine);
  };

  // Always use line.x/y for consistency - don't switch to calculated offset
  // groupX and groupY are already defined above

  // Add null check to prevent rendering invalid line data
  if (!line || !line.id || !currentPoints || currentPoints.length < 4) {
    return null;
  }

  // Define group position from line coordinates
  const groupX = line.x || 0;
  const groupY = line.y || 0;

  return (
    <Group
      id={id}
      name={line.name}
      x={groupX}
      y={groupY}
      scaleX={1}
      scaleY={1}
      draggable={isDraggable}
      visible={true}
      listening={true}
      onDragStart={(e) => {
        isDraggingRef.current = true;
        e.target.setAttr("dragDistance", 0);
      }}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
      onClick={handleLineSelect}
      ref={groupRef}
      perfectDrawEnabled={false}
      shadowForStrokeEnabled={false}
    >
      {/* Conditionally render Line or Arrow based on arrow head properties */}
      {line.pointerAtBeginning || line.pointerAtEnd ? (
        <Arrow
          id={id}
          name={line.name}
          points={currentPoints}
          stroke={line.stroke || strokeColor}
          fill={line.stroke || strokeColor}
          strokeWidth={strokeWidth}
          hitStrokeWidth={Math.max(10, strokeWidth + 8)}
          listening={true}
          perfectDrawEnabled={false}
          shadowForStrokeEnabled={false}
          lineCap="round"
          lineJoin="round"
          dash={isDashed ? [10, 5] : []}
          pointerAtBeginning={line.pointerAtBeginning || false}
          pointerAtEnd={line.pointerAtEnd || false}
          onClick={(e) => {
            if (!isPrimaryMouseButton(e.evt)) {
              return;
            }
            e.cancelBubble = true;
            // Set type in event target attrs before handling selection
            const pointerAtBeginning = line.pointerAtBeginning || false;
            const pointerAtEnd = line.pointerAtEnd || false;
            if (pointerAtBeginning && pointerAtEnd) {
              e.target.attrs.type = "DoubleSidedArrow";
            } else if (pointerAtBeginning || pointerAtEnd) {
              e.target.attrs.type = "Arrow";
            } else {
              e.target.attrs.type = "Line";
            }
            handleLineSelect(e);
          }}
          ref={lineRef}
        />
      ) : (
        <Line
          id={id}
          name={line.name}
          points={currentPoints}
          stroke={line.stroke || strokeColor}
          strokeWidth={strokeWidth}
          hitStrokeWidth={Math.max(10, strokeWidth + 8)}
          listening={true}
          perfectDrawEnabled={false}
          shadowForStrokeEnabled={false}
          tension={0}
          lineCap="round"
          lineJoin="round"
          dash={isDashed ? [10, 5] : []}
          onClick={(e) => {
            if (!isPrimaryMouseButton(e.evt)) {
              return;
            }
            e.cancelBubble = true;
            // Set type as Line when no arrow heads
            e.target.attrs.type = "Line";
            handleLineSelect(e);
          }}
          ref={lineRef}
        />
      )}
      {/* Only show dots when line is selected */}
      {isSelected && startDot && (
        <Circle
          name={line.name}
          x={currentPoints[0]}
          y={currentPoints[1]}
          radius={startDot.radius}
          fill={startDot.fill}
          stroke={startDot.stroke}
          strokeWidth={Math.max(
            0,
            (startDot.strokeWidth || 0) / Math.max(0.3, effectiveZoomLevel || 1)
          )}
          draggable={isDraggable}
          onDragStart={(e) => e.target.setAttr("dragDistance", 0)}
          onDragMove={(e) => {
            let newX = e.target.x();
            let newY = e.target.y();

            // Apply boundary constraints for main canvas before updating
            // Get current group position (use ref during drag, fallback to prop)
            const groupX = groupRef.current?.x() ?? line.x ?? 0;
            const groupY = groupRef.current?.y() ?? line.y ?? 0;

            if (!isOffCanvas && canvasWidth && canvasHeight) {
              const canvasPadding = 20;

              // Calculate absolute position of the dragged point
              const absPointX = newX + groupX;
              const absPointY = newY + groupY;

              // Constrain the point within canvas boundaries
              const constrainedAbsX = Math.max(
                canvasPadding,
                Math.min(canvasWidth - canvasPadding, absPointX)
              );
              const constrainedAbsY = Math.max(
                canvasPadding,
                Math.min(canvasHeight - canvasPadding, absPointY)
              );

              // Convert back to relative coordinates
              newX = constrainedAbsX - groupX;
              newY = constrainedAbsY - groupY;

              // Update the circle's visual position to match the constrained position
              e.target.x(newX);
              e.target.y(newY);
            }

            // Calculate and store width/height during drag move
            const tempPoints = [...currentPoints];
            tempPoints[0] = newX;
            tempPoints[1] = newY;

            // Calculate absolute points for width/height
            const absX1 = tempPoints[0] + groupX;
            const absY1 = tempPoints[1] + groupY;
            const absX2 = tempPoints[2] + groupX;
            const absY2 = tempPoints[3] + groupY;

            const width = Math.abs(absX2 - absX1);
            const height = Math.abs(absY2 - absY1);

            // Store the calculated width/height
            storedWidthHeightRef.current = { width, height };

            updatePoint(0, newX, newY);
            // Update the visual position of the circle
            e.target.x(currentPoints[0]);
            e.target.y(currentPoints[1]);
          }}
          onDragEnd={(e) => {
            // Prevent event bubbling to avoid triggering line group onDragEnd
            e.cancelBubble = true;

            // Update the line state with the final position and dimensions
            if (onDotDragMove) {
              const groupX = groupRef.current?.x() || 0;
              const groupY = groupRef.current?.y() || 0;
              const { width, height } = storedWidthHeightRef.current;

              onDotDragMove({
                ...line,
                points: currentPointsRef.current,
                x: groupX,
                y: groupY,
                width,
                height,
                skipOffset: true,
              });
            }

            // CRITICAL: Mark that there are pending changes so history will be saved on unselection
            if (isElementBeingEdited) {
              setPendingChanges(true);
            }

            // CRITICAL: Don't save to history here - history will be saved when element is unselected
            // This groups all drag and transform changes into a single history entry
          }}
          ref={startDotRef}
        />
      )}
      {isSelected && endDot && (
        <Circle
          name={line.name}
          x={currentPoints[2]}
          y={currentPoints[3]}
          radius={endDot.radius}
          fill={endDot.fill}
          stroke={endDot.stroke}
          strokeWidth={Math.max(
            0,
            (endDot.strokeWidth || 0) / Math.max(0.3, effectiveZoomLevel || 1)
          )}
          draggable={isDraggable}
          onDragStart={(e) => e.target.setAttr("dragDistance", 0)}
          onDragMove={(e) => {
            let newX = e.target.x();
            let newY = e.target.y();

            // Apply boundary constraints for main canvas before updating
            // Get current group position (use ref during drag, fallback to prop)
            const groupX = groupRef.current?.x() ?? line.x ?? 0;
            const groupY = groupRef.current?.y() ?? line.y ?? 0;

            if (!isOffCanvas && canvasWidth && canvasHeight) {
              const canvasPadding = 20;

              // Calculate absolute position of the dragged point
              const absPointX = newX + groupX;
              const absPointY = newY + groupY;

              // Constrain the point within canvas boundaries
              const constrainedAbsX = Math.max(
                canvasPadding,
                Math.min(canvasWidth - canvasPadding, absPointX)
              );
              const constrainedAbsY = Math.max(
                canvasPadding,
                Math.min(canvasHeight - canvasPadding, absPointY)
              );

              // Convert back to relative coordinates
              newX = constrainedAbsX - groupX;
              newY = constrainedAbsY - groupY;

              // Update the circle's visual position to match the constrained position
              e.target.x(newX);
              e.target.y(newY);
            }

            // Calculate and store width/height during drag move
            const tempPoints = [...currentPoints];
            tempPoints[2] = newX;
            tempPoints[3] = newY;

            // Calculate absolute points for width/height
            const absX1 = tempPoints[0] + groupX;
            const absY1 = tempPoints[1] + groupY;
            const absX2 = tempPoints[2] + groupX;
            const absY2 = tempPoints[3] + groupY;

            const width = Math.abs(absX2 - absX1);
            const height = Math.abs(absY2 - absY1);

            // Store the calculated width/height
            storedWidthHeightRef.current = { width, height };

            updatePoint(2, newX, newY);
            // Update the visual position of the circle
            e.target.x(currentPoints[2]);
            e.target.y(currentPoints[3]);
          }}
          onDragEnd={(e) => {
            // Prevent event bubbling to avoid triggering line group onDragEnd
            e.cancelBubble = true;

            // Update the line state with the final position and dimensions
            if (onDotDragMove) {
              const groupX = groupRef.current?.x() || 0;
              const groupY = groupRef.current?.y() || 0;
              const { width, height } = storedWidthHeightRef.current;

              onDotDragMove({
                ...line,
                points: currentPointsRef.current,
                x: groupX,
                y: groupY,
                width,
                height,
                skipOffset: true,
              });
            }

            // CRITICAL: Mark that there are pending changes so history will be saved on unselection
            if (isElementBeingEdited) {
              setPendingChanges(true);
            }

            // CRITICAL: Don't save to history here - history will be saved when element is unselected
            // This groups all drag and transform changes into a single history entry
          }}
          ref={endDotRef}
        />
      )}

      {/* Blue waypoint circles for bends - show all waypoints when selected */}
      {isSelected &&
        currentPoints.map((_, index) =>
          index % 2 === 0 ? (
            <Circle
              key={`waypoint-${index}`}
              x={currentPoints[index]}
              y={currentPoints[index + 1]}
              radius={6}
              fill="blue"
              name={line.name}
              stroke="white"
              strokeWidth={Math.max(
                1.5,
                1.5 / Math.max(0.3, effectiveZoomLevel || 1)
              )}
              perfectDrawEnabled={false}
              shadowForStrokeEnabled={false}
              draggable={isDraggable}
              onDragStart={(e) => {
                e.cancelBubble = true;
              }}
              onDragMove={(e) => {
                e.cancelBubble = true;

                // Get the local position of the dragged circle
                let localX = e.target.x();
                let localY = e.target.y();

                // Apply boundary constraints for main canvas before updating
                const groupX = groupRef.current?.x() || line.x || 0;
                const groupY = groupRef.current?.y() || line.y || 0;

                if (!isOffCanvas && canvasWidth && canvasHeight) {
                  const canvasPadding = 20;

                  // Calculate absolute position of the dragged point
                  const absPointX = localX + groupX;
                  const absPointY = localY + groupY;

                  // Constrain the point within canvas boundaries
                  const constrainedAbsX = Math.max(
                    canvasPadding,
                    Math.min(canvasWidth - canvasPadding, absPointX)
                  );
                  const constrainedAbsY = Math.max(
                    canvasPadding,
                    Math.min(canvasHeight - canvasPadding, absPointY)
                  );

                  // Convert back to relative coordinates
                  localX = constrainedAbsX - groupX;
                  localY = constrainedAbsY - groupY;

                  // Update the circle's visual position to match the constrained position
                  e.target.x(localX);
                  e.target.y(localY);
                }

                // Calculate and store width/height during drag move
                const tempPoints = [...currentPoints];
                tempPoints[index] = localX;
                tempPoints[index + 1] = localY;

                // Calculate width/height from all points
                let minX = Infinity,
                  minY = Infinity,
                  maxX = -Infinity,
                  maxY = -Infinity;
                for (let i = 0; i < tempPoints.length; i += 2) {
                  const absX = tempPoints[i] + groupX;
                  const absY = tempPoints[i + 1] + groupY;
                  minX = Math.min(minX, absX);
                  maxX = Math.max(maxX, absX);
                  minY = Math.min(minY, absY);
                  maxY = Math.max(maxY, absY);
                }

                const width = maxX - minX;
                const height = maxY - minY;

                // Store the calculated width/height
                storedWidthHeightRef.current = { width, height };

                // Use the existing updatePoint function for consistency
                updatePoint(index, localX, localY);
              }}
              onDragEnd={(e) => {
                e.cancelBubble = true;

                // Ensure circle position is constrained (in case it wasn't updated in onDragMove)
                let localX = e.target.x();
                let localY = e.target.y();
                const groupX = groupRef.current?.x() || line.x || 0;
                const groupY = groupRef.current?.y() || line.y || 0;

                if (!isOffCanvas && canvasWidth && canvasHeight) {
                  const canvasPadding = 20;

                  // Calculate absolute position of the dragged point
                  const absPointX = localX + groupX;
                  const absPointY = localY + groupY;

                  // Constrain the point within canvas boundaries
                  const constrainedAbsX = Math.max(
                    canvasPadding,
                    Math.min(canvasWidth - canvasPadding, absPointX)
                  );
                  const constrainedAbsY = Math.max(
                    canvasPadding,
                    Math.min(canvasHeight - canvasPadding, absPointY)
                  );

                  // Convert back to relative coordinates
                  localX = constrainedAbsX - groupX;
                  localY = constrainedAbsY - groupY;

                  // Update the circle's visual position to match the constrained position
                  e.target.x(localX);
                  e.target.y(localY);
                }

                // Update the line state with the final position and dimensions
                if (onDotDragMove) {
                  const { width, height } = storedWidthHeightRef.current;

                  onDotDragMove({
                    ...line,
                    points: currentPointsRef.current,
                    x: groupX,
                    y: groupY,
                    width,
                    height,
                    skipOffset: true,
                  });
                }

                // CRITICAL: Mark that there are pending changes so history will be saved on unselection
                if (isElementBeingEdited) {
                  setPendingChanges(true);
                }

                // CRITICAL: Don't save to history here - history will be saved when element is unselected
                // This groups all drag and transform changes into a single history entry
              }}
            />
          ) : null
        )}

      {/* Copy button for off-canvas lines */}
      {(() => {
        // Copy button commented out
        const shouldShowCopyButton = false &&
          isSelected &&
          !isPreview &&
          isOffCanvas &&
          typeof onCopyToOppositeCanvas === "function";

        if (!shouldShowCopyButton) return null;

        // Calculate bounding box for line (absolutePoints are relative to Group)
        if (!absolutePoints || absolutePoints.length < 4) return null;
        const xCoords = absolutePoints.filter((_, i) => i % 2 === 0);
        const yCoords = absolutePoints.filter((_, i) => i % 2 === 1);
        const minX = Math.min(...xCoords);
        const maxX = Math.max(...xCoords);
        const minY = Math.min(...yCoords);
        const maxY = Math.max(...yCoords);

        // Position relative to the Group (line.x and line.y are the Group position)
        // Button should be at top-right of the line's bounding box
        const copyButtonX = maxX - 14; // Right edge of line minus button width
        const copyButtonY = minY - 36; // Above the line

        return (
          <Html
            x={copyButtonX}
            y={copyButtonY}
            divProps={{
              style: {
                pointerEvents: "none",
                zIndex: 30,
              },
            }}
          >
            <div
              style={{
                pointerEvents: "none",
              }}
            >
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onCopyToOppositeCanvas?.();
                }}
                title="Copy to Main Canvas"
                style={{
                  width: "26px",
                  height: "26px",
                  borderRadius: "4px",
                  border: "1px solid #CBD5E1",
                  backgroundColor: "#FFFFFF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 10px rgba(15, 23, 42, 0.18)",
                  cursor: "pointer",
                  pointerEvents: "auto",
                  padding: 0,
                  transition:
                    "background-color 0.15s ease, border-color 0.15s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#F1F5F9";
                  e.currentTarget.style.borderColor = "#94A3B8";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#FFFFFF";
                  e.currentTarget.style.borderColor = "#CBD5E1";
                }}
              >
                <svg
                  width="16"
                  height="19"
                  viewBox="0 0 17 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5.97572 15.75C5.47055 15.75 5.04297 15.575 4.69297 15.225C4.34297 14.875 4.16797 14.4474 4.16797 13.9423V2.55775C4.16797 2.05258 4.34297 1.625 4.69297 1.275C5.04297 0.925 5.47055 0.75 5.97572 0.75H14.3602C14.8654 0.75 15.293 0.925 15.643 1.275C15.993 1.625 16.168 2.05258 16.168 2.55775V13.9423C16.168 14.4474 15.993 14.875 15.643 15.225C15.293 15.575 14.8654 15.75 14.3602 15.75H5.97572ZM5.97572 14.25H14.3602C14.4372 14.25 14.5077 14.2179 14.5717 14.1538C14.6359 14.0898 14.668 14.0192 14.668 13.9423V2.55775C14.668 2.48075 14.6359 2.41025 14.5717 2.34625C14.5077 2.28208 14.4372 2.25 14.3602 2.25H5.97572C5.89872 2.25 5.82822 2.28208 5.76422 2.34625C5.70005 2.41025 5.66797 2.48075 5.66797 2.55775V13.9423C5.66797 14.0192 5.70005 14.0898 5.76422 14.1538C5.82822 14.2179 5.89872 14.25 5.97572 14.25ZM2.47572 19.25C1.97055 19.25 1.54297 19.075 1.19297 18.725C0.842969 18.375 0.667969 17.9474 0.667969 17.4423V4.55775H2.16797V17.4423C2.16797 17.5193 2.20005 17.5898 2.26422 17.6538C2.32822 17.7179 2.39872 17.75 2.47572 17.75H12.3602V19.25H2.47572Z"
                    fill="#1E293B"
                  />
                </svg>
              </button>
            </div>
          </Html>
        );
      })()}
    </Group>
  );
};

LineElement.propTypes = {
  line: PropTypes.shape({
    id: PropTypes.string.isRequired,
    points: PropTypes.arrayOf(PropTypes.number).isRequired,
    strokeColor: PropTypes.string,
    strokeWidth: PropTypes.number,
    isDashed: PropTypes.bool,
    startDot: PropTypes.object,
    endDot: PropTypes.object,
    x: PropTypes.number,
    y: PropTypes.number,
    length: PropTypes.number,
    angle: PropTypes.number,
  }).isRequired,
  isDraggable: PropTypes.bool,
  onClick: PropTypes.func,
  onDragEnd: PropTypes.func.isRequired,
  onSelect: PropTypes.func,
  isSelected: PropTypes.bool,
  onPointsUpdate: PropTypes.func,
  saveToHistory: PropTypes.func,
  onDotDragMove: PropTypes.func,
  setSelectedObject: PropTypes.func,
  canvasWidth: PropTypes.number,
  canvasHeight: PropTypes.number,
};

LineElement.defaultProps = {
  isDraggable: false,
  onClick: () => {},
  onSelect: () => {},
  isSelected: false,
  onPointsUpdate: () => {},
  saveToHistory: () => {},
  onDotDragMove: () => {},
  setSelectedObject: () => {},
};

export default LineElement;
